import {ServiceField} from "@/lib/constants/service.ts";
import {MonitoringServiceType} from "@/lib/types/checks.ts";
import {FieldType} from "@/pages/checks/components/form/fields/common.ts";

type ExcludedAutogeneratedServiceFields =
  | ServiceField.URL_SCHEME
  | ServiceField.URL_PATH
  | ServiceField.SCRIPT
  | ServiceField.INTERVAL
  | ServiceField.TAGS
  | ServiceField.NAME
  | ServiceField.ID
  | ServiceField.CONTACT_GROUPS
  | ServiceField.LOCATION
  | ServiceField.SCHEDULE_STATE
  | ServiceField.SCHEDULE_ENTRIES
  | ServiceField.ESCALATIONS
  | ServiceField.CLOUD_STATUS;

export type AutogeneratedServiceFields = Exclude<ServiceField, ExcludedAutogeneratedServiceFields>;

export const FIELD_TYPES: Record<AutogeneratedServiceFields, FieldType> = {
  // text
  [ServiceField.ADDRESS]: FieldType.Text,
  [ServiceField.USERNAME]: FieldType.Text,
  [ServiceField.SEND_STRING]: FieldType.Text,
  [ServiceField.EXPECT_STRING]: FieldType.Text,
  [ServiceField.PROXY]: FieldType.Text,
  [ServiceField.STATUS_CODE]: FieldType.Text,
  [ServiceField.RUM_APDEX_THRESHOLD]: FieldType.Text,
  [ServiceField.RUM_EXCLUDE_GROUP_PARAMS]: FieldType.Text,
  [ServiceField.SSL_FINGERPRINT]: FieldType.Text,
  [ServiceField.SSL_FILE]: FieldType.Text,
  [ServiceField.SSL_ISSUER]: FieldType.Text,
  [ServiceField.SSL_RESOLVE]: FieldType.Text,
  [ServiceField.THRESHOLD]: FieldType.Text,
  [ServiceField.PORT]: FieldType.Text,
  [ServiceField.RESPONSE_TIME_SLA]: FieldType.Text,
  [ServiceField.DNS_SERVER]: FieldType.Text,
  [ServiceField.HTTP_EXPECTED_CHECKSUM]: FieldType.Text,
  [ServiceField.UPTIME_SLA]: FieldType.Percentage,
  // select
  [ServiceField.ENCRYPTION]: FieldType.Select,
  [ServiceField.EXPECT_STRING_TYPE]: FieldType.Select,
  [ServiceField.DNS_RECORD_TYPE]: FieldType.Select,
  [ServiceField.PAGESPEED_CONNECTION_THROTTLING]: FieldType.Select,
  [ServiceField.PAGESPEED_EMULATED_DEVICES]: FieldType.Select,
  [ServiceField.PAGESPEED_GRADE_THRESHOLD]: FieldType.Select,
  [ServiceField.RUM_AGGREGATION_TYPE]: FieldType.Select,
  [ServiceField.SSL_PROTOCOL]: FieldType.Select,
  [ServiceField.SSL_MIN_VERSION]: FieldType.Select,
  [ServiceField.GROUP_CHECK_DOWN_CONDITION]: FieldType.Select,
  [ServiceField.GROUP_RESPONSE_TIME_CALCULATION_MODE]: FieldType.Select,
  [ServiceField.NUM_RETRIES]: FieldType.Select,
  [ServiceField.SENSITIVITY]: FieldType.Select,
  [ServiceField.USE_IP_VERSION]: FieldType.Select,
  [ServiceField.GROUP_RESPONSE_TIME_CHECK_TYPE]: FieldType.Select,
  [ServiceField.THIRD_PARTY_GROUP]: FieldType.Select,
  [ServiceField.THIRD_PARTY_COMPONENTS]: FieldType.Select,
  [ServiceField.HTTP_VERSION]: FieldType.Select,
  [ServiceField.HTTP_CHECKSUM_ALGORITHM]: FieldType.Select,
  // textarea
  [ServiceField.HEADERS]: FieldType.TextArea,
  [ServiceField.PAGESPEED_EXCLUDE_URLS]: FieldType.TextArea,
  [ServiceField.RUM_EXCLUDE_USER_AGENTS]: FieldType.TextArea,
  [ServiceField.RUM_EXTERNAL_DOMAINS]: FieldType.TextArea,
  [ServiceField.RUM_URL_GROUPS]: FieldType.TextArea,
  [ServiceField.SSL_MATCH]: FieldType.TextArea,
  [ServiceField.NOTES]: FieldType.TextArea,
  [ServiceField.FTP_PATHS]: FieldType.TextArea,
  [ServiceField.SFTP_PRIVATE_KEY]: FieldType.TextArea,
  [ServiceField.SFTP_KNOWN_HOSTS]: FieldType.TextArea,
  [ServiceField.HTTP_DNS_SERVERS]: FieldType.TextArea,
  [ServiceField.HTTP_CONNECT_TO]: FieldType.TextArea,
  // checkbox
  [ServiceField.RUM_IS_AJAX_DISABLED]: FieldType.CheckBox,
  [ServiceField.SSL_CRL]: FieldType.CheckBox,
  [ServiceField.SSL_SELFSIGNED]: FieldType.CheckBox,
  [ServiceField.SSL_FIRST_ELEMENT]: FieldType.CheckBox,
  [ServiceField.SSL_IGNORE_AUTHORITY_WARNINGS]: FieldType.CheckBox,
  [ServiceField.SSL_IGNORE_SCT]: FieldType.CheckBox,
  [ServiceField.INCLUDE_IN_GLOBAL_METRICS]: FieldType.CheckBox,
  [ServiceField.FTP_EXPLICIT_TLS]: FieldType.CheckBox,
  [ServiceField.FTP_SKIP_CERT_VERIFICATION]: FieldType.CheckBox,
  [ServiceField.NOTIFY_ONLY_ON_DOWN]: FieldType.CheckBox,
  [ServiceField.HTTP_INSECURE_DNS]: FieldType.CheckBox,
  // password
  [ServiceField.PASSWORD]: FieldType.Password,
  [ServiceField.SFTP_PASSPHRASE]: FieldType.Password,
  // radiogroup
  [ServiceField.GROUP_UPTIME_PERCENT_CALCULATION]: FieldType.Radio,
  [ServiceField.THIRD_PARTY_MONITORING_TYPE]: FieldType.Radio,
  // tags
  [ServiceField.GROUP_CHECK_TAGS]: FieldType.Tags,
  // custom
  // checks selector hack
  [ServiceField.GROUP_CHECK_SERVICES]: FieldType.MultiCheckSelector,
  [ServiceField.GROUP_RESPONSE_TIME_SINGLE_CHECK]: FieldType.SingleCheckSelector,
  [ServiceField.VERSION]: FieldType.Version,
  // maintenance v2
  [ServiceField.IS_UNDER_MANUAL_MAINTENANCE]: FieldType.CheckBox,
  [ServiceField.PAUSE_UNDER_MANUAL_MAINTENANCE]: FieldType.CheckBox,
  [ServiceField.MAINTENANCE_SCHEDULE_IDS]: FieldType.Array,
  [ServiceField.GLOBAL_PING_NETWORK_CAPTURE_TRACEROUTE]: FieldType.CheckBox,
  [ServiceField.GLOBAL_PING_NETWORK_NO_PROBE_FALLBACK]: FieldType.Radio,
  [ServiceField.GLOBAL_PING_NETWORK_NAME]: FieldType.TextArea,
  [ServiceField.GLOBAL_PING_NETWORK_LOCATIONS]: FieldType.Array,
};

export enum QUERY {
  MONITORING_SERVICE_TYPE = "monitoring_service_type",
  GROUP_CHECK_SERVICES = "group_check_services",
  TAB = "tab",
  DUPLICATE = "duplicate",
}

export enum State {
  Up = "Up",
  Down = "Down",
  Pending = "Pending",
  Paused = "Paused",
}

export const baseSections = {
  general: {
    id: "general",
    title: "General",
    isVisible: true,
    fields: [
      ServiceField.NAME,
      ServiceField.INTERVAL,
      ServiceField.ADDRESS,
      ServiceField.RUM_AGGREGATION_TYPE,
      ServiceField.RUM_APDEX_THRESHOLD,
      ServiceField.SSL_PROTOCOL,
      ServiceField.DNS_RECORD_TYPE,
      ServiceField.DNS_SERVER,
      ServiceField.LOCATION,
      ServiceField.CONTACT_GROUPS,
      ServiceField.TAGS,
      ServiceField.CLOUD_STATUS,
      ServiceField.NOTIFY_ONLY_ON_DOWN,
      ServiceField.THIRD_PARTY_GROUP,
      ServiceField.THIRD_PARTY_COMPONENTS,
      ServiceField.THIRD_PARTY_MONITORING_TYPE,
      ServiceField.GLOBAL_PING_NETWORK_LOCATIONS,
      ServiceField.GLOBAL_PING_NETWORK_NAME,
      ServiceField.GLOBAL_PING_NETWORK_CAPTURE_TRACEROUTE,
      ServiceField.GLOBAL_PING_NETWORK_NO_PROBE_FALLBACK,
    ],
  },
  checkSteps: {
    id: "checkSteps",
    title: "Check Steps",
    isVisible: false,
    fields: [
      ServiceField.SCRIPT,
      ServiceField.PAGESPEED_EXCLUDE_URLS,
      ServiceField.PAGESPEED_EMULATED_DEVICES,
      ServiceField.PAGESPEED_CONNECTION_THROTTLING,
      ServiceField.PAGESPEED_GRADE_THRESHOLD,
    ],
  },
  validationnsecurity: {
    id: "validationnsecurity",
    title: "Validation & Security",
    isVisible: false,
    fields: [
      ServiceField.EXPECT_STRING_TYPE,
      ServiceField.PROXY,
      ServiceField.STATUS_CODE,
      ServiceField.FTP_SKIP_CERT_VERIFICATION,
      ServiceField.FTP_PATHS,
      ServiceField.FTP_EXPLICIT_TLS,
      ServiceField.SFTP_PRIVATE_KEY,
      ServiceField.SFTP_PASSPHRASE,
      ServiceField.SFTP_KNOWN_HOSTS,
      ServiceField.HTTP_CHECKSUM_ALGORITHM,
      ServiceField.HTTP_EXPECTED_CHECKSUM,
    ],
  },
  aggregationnalerts: {
    id: "aggregationnalerts",
    title: "Checks & Alerts",
    isVisible: false,
    fields: [
      ServiceField.GROUP_CHECK_SERVICES,
      ServiceField.GROUP_CHECK_TAGS,
      ServiceField.GROUP_UPTIME_PERCENT_CALCULATION,
      ServiceField.GROUP_CHECK_DOWN_CONDITION,
      ServiceField.GROUP_RESPONSE_TIME_CALCULATION_MODE,
    ],
  },
  sensitivityAndTargeting: {
    id: "sensitivityAndTargeting",
    title: "Execution & SLA",
    isVisible: true,
    fields: [
      ServiceField.SENSITIVITY,
      ServiceField.NUM_RETRIES,
      ServiceField.USE_IP_VERSION,
      ServiceField.VERSION,
      ServiceField.UPTIME_SLA,
      ServiceField.RESPONSE_TIME_SLA,
      ServiceField.NOTES,
      ServiceField.INCLUDE_IN_GLOBAL_METRICS,
      ServiceField.SSL_RESOLVE,
      ServiceField.SSL_MIN_VERSION,
      ServiceField.SSL_FINGERPRINT,
      ServiceField.SSL_ISSUER,
      ServiceField.SSL_MATCH,
      ServiceField.SSL_CRL,
      ServiceField.SSL_SELFSIGNED,
      ServiceField.SSL_FIRST_ELEMENT,
      ServiceField.SSL_IGNORE_AUTHORITY_WARNINGS,
      ServiceField.SSL_IGNORE_SCT,
      ServiceField.RUM_EXCLUDE_USER_AGENTS,
      ServiceField.RUM_EXTERNAL_DOMAINS,
      ServiceField.RUM_URL_GROUPS,
      ServiceField.RUM_IS_AJAX_DISABLED,
      ServiceField.RUM_EXCLUDE_GROUP_PARAMS,
      ServiceField.HTTP_VERSION,
      ServiceField.HTTP_DNS_SERVERS,
      ServiceField.HTTP_INSECURE_DNS,
      ServiceField.HTTP_CONNECT_TO,
    ],
  },
  escalations: {id: "escalations", title: "Escalations", isVisible: true, fields: [ServiceField.ESCALATIONS]},
  maintenance_v2: {
    id: "maintenance_v2",
    title: "Maintenance",
    isVisible: true,
    fields: [ServiceField.IS_UNDER_MANUAL_MAINTENANCE, ServiceField.PAUSE_UNDER_MANUAL_MAINTENANCE],
  },
};

export const advancedFields: Record<string, Record<string, string>> = {
  [ServiceField.USERNAME]: {
    [MonitoringServiceType.PAGESPEED]: baseSections.checkSteps.id,
    default: baseSections.validationnsecurity.id,
  },
  [ServiceField.PASSWORD]: {
    [MonitoringServiceType.PAGESPEED]: baseSections.checkSteps.id,
    default: baseSections.validationnsecurity.id,
  },
  [ServiceField.ENCRYPTION]: {
    [MonitoringServiceType.TCP]: baseSections.sensitivityAndTargeting.id,
    [MonitoringServiceType.POP]: baseSections.sensitivityAndTargeting.id,
    [MonitoringServiceType.IMAP]: baseSections.sensitivityAndTargeting.id,
    [MonitoringServiceType.SMTP]: baseSections.sensitivityAndTargeting.id,
    default: baseSections.validationnsecurity.id,
  },
  [ServiceField.SEND_STRING]: {
    [MonitoringServiceType.TCP]: baseSections.sensitivityAndTargeting.id,
    [MonitoringServiceType.UDP]: baseSections.sensitivityAndTargeting.id,
    default: baseSections.validationnsecurity.id,
  },
  [ServiceField.EXPECT_STRING]: {
    [MonitoringServiceType.TCP]: baseSections.sensitivityAndTargeting.id,
    [MonitoringServiceType.UDP]: baseSections.sensitivityAndTargeting.id,
    [MonitoringServiceType.HTTP]: baseSections.validationnsecurity.id,
    [MonitoringServiceType.IMAP]: baseSections.sensitivityAndTargeting.id,
    [MonitoringServiceType.POP]: baseSections.sensitivityAndTargeting.id,
    [MonitoringServiceType.SMTP]: baseSections.sensitivityAndTargeting.id,
    default: baseSections.general.id,
  },
  [ServiceField.PORT]: {
    [MonitoringServiceType.IMAP]: baseSections.sensitivityAndTargeting.id,
    [MonitoringServiceType.POP]: baseSections.sensitivityAndTargeting.id,
    [MonitoringServiceType.SMTP]: baseSections.sensitivityAndTargeting.id,
    [MonitoringServiceType.SSL_CERT]: baseSections.sensitivityAndTargeting.id,
    default: baseSections.general.id,
  },
  [ServiceField.THRESHOLD]: {
    [MonitoringServiceType.SSL_CERT]: baseSections.general.id,
    [MonitoringServiceType.WHOIS]: baseSections.general.id,
    [MonitoringServiceType.RDAP]: baseSections.general.id,
    default: baseSections.sensitivityAndTargeting.id,
  },
  [ServiceField.HEADERS]: {
    [MonitoringServiceType.PAGESPEED]: baseSections.checkSteps.id,
    default: baseSections.validationnsecurity.id,
  },
};

enum SENSITIVITY_AND_TARGETING_SECTIONS {
  EXECUTION = "execution",
  ADVANCED = "advanced",
  SLA = "sla",
  TRACKING = "tracking",
  CERTIFICATE_VALIDATION = "certificate_validation",
}

enum FIELD_SIZE {
  XS = "XS",
  S = "S",
  M = "M",
  L = "L",
  XL = "XL",
}

const SensitivityAndTargetingSectionTitles = {
  [SENSITIVITY_AND_TARGETING_SECTIONS.EXECUTION]: "Execution Settings",
  [SENSITIVITY_AND_TARGETING_SECTIONS.ADVANCED]: "Advanced Settings",
  [SENSITIVITY_AND_TARGETING_SECTIONS.SLA]: "SLA Targets",
  [SENSITIVITY_AND_TARGETING_SECTIONS.TRACKING]: "Tracking Settings",
  [SENSITIVITY_AND_TARGETING_SECTIONS.CERTIFICATE_VALIDATION]: "Certificate & Validation",
};

export const noWrapFields = [ServiceField.NUM_RETRIES, ServiceField.RESPONSE_TIME_SLA];

export const FIELD_PLACEHOLDERS: Partial<Record<ServiceField, string>> = {
  [ServiceField.HTTP_CONNECT_TO]: "example.com:443:192.0.2.10:443",
  [ServiceField.HTTP_DNS_SERVERS]: "1.1.1.1\ntls://dns.example.com",
  [ServiceField.HTTP_EXPECTED_CHECKSUM]: "e3b0c44298fc1c149afbf4c8996fb92427ae41e4649b934ca495991b7852b855",
};

export const sensitivityAndTargetingFields = {
  [MonitoringServiceType.HTTP]: [
    {
      section: SENSITIVITY_AND_TARGETING_SECTIONS.EXECUTION,
      title: SensitivityAndTargetingSectionTitles[SENSITIVITY_AND_TARGETING_SECTIONS.EXECUTION],
      rows: [
        {
          size: null,
          fields: [
            {field: ServiceField.SENSITIVITY, size: FIELD_SIZE.S},
            {field: ServiceField.NUM_RETRIES, size: FIELD_SIZE.XS},
          ],
          warnings: [ServiceField.SENSITIVITY, ServiceField.NUM_RETRIES],
        },
        {
          size: null,
          fields: [
            {field: ServiceField.USE_IP_VERSION, size: FIELD_SIZE.S},
            {field: ServiceField.THRESHOLD, size: FIELD_SIZE.XS},
            {field: ServiceField.VERSION, size: FIELD_SIZE.S},
          ],
        },
      ],
    },
    {
      section: SENSITIVITY_AND_TARGETING_SECTIONS.ADVANCED,
      title: SensitivityAndTargetingSectionTitles[SENSITIVITY_AND_TARGETING_SECTIONS.ADVANCED],
      collapsible: true,
      rows: [
        {size: null, fields: [{field: ServiceField.HTTP_VERSION, size: FIELD_SIZE.M}]},
        {size: null, fields: [{field: ServiceField.HTTP_DNS_SERVERS, size: FIELD_SIZE.L}]},
        {size: null, fields: [{field: ServiceField.HTTP_INSECURE_DNS, size: FIELD_SIZE.L}]},
        {size: null, fields: [{field: ServiceField.HTTP_CONNECT_TO, size: FIELD_SIZE.L}]},
      ],
    },
    {
      section: SENSITIVITY_AND_TARGETING_SECTIONS.SLA,
      title: SensitivityAndTargetingSectionTitles[SENSITIVITY_AND_TARGETING_SECTIONS.SLA],
      rows: [
        {
          size: null,
          fields: [
            {field: ServiceField.UPTIME_SLA, size: FIELD_SIZE.XS},
            {field: ServiceField.RESPONSE_TIME_SLA, size: FIELD_SIZE.S},
          ],
        },
        {size: null, fields: [{field: ServiceField.NOTES, size: FIELD_SIZE.L}]},
        {size: null, fields: [{field: ServiceField.INCLUDE_IN_GLOBAL_METRICS, size: FIELD_SIZE.L}]},
      ],
    },
  ],
  [MonitoringServiceType.GROUP]: [
    {
      section: SENSITIVITY_AND_TARGETING_SECTIONS.SLA,
      title: SensitivityAndTargetingSectionTitles[SENSITIVITY_AND_TARGETING_SECTIONS.SLA],
      rows: [
        {
          size: FIELD_SIZE.XL,
          fields: [
            {field: ServiceField.UPTIME_SLA, size: null},
            {field: ServiceField.RESPONSE_TIME_SLA, size: null},
          ],
        },
        {size: null, fields: [{field: ServiceField.NOTES, size: FIELD_SIZE.XL}]},
        {size: null, fields: [{field: ServiceField.INCLUDE_IN_GLOBAL_METRICS, size: FIELD_SIZE.L}]},
      ],
    },
  ],
  [MonitoringServiceType.BLACKLIST]: [
    {
      section: SENSITIVITY_AND_TARGETING_SECTIONS.EXECUTION,
      title: SensitivityAndTargetingSectionTitles[SENSITIVITY_AND_TARGETING_SECTIONS.EXECUTION],
      rows: [
        {
          size: null,
          fields: [{field: ServiceField.NUM_RETRIES, size: FIELD_SIZE.S}],
          warnings: [ServiceField.NUM_RETRIES],
        },
      ],
    },
    {
      section: SENSITIVITY_AND_TARGETING_SECTIONS.SLA,
      title: SensitivityAndTargetingSectionTitles[SENSITIVITY_AND_TARGETING_SECTIONS.SLA],
      rows: [
        {
          size: null,
          fields: [{field: ServiceField.UPTIME_SLA, size: FIELD_SIZE.XS}],
        },
        {size: null, fields: [{field: ServiceField.NOTES, size: FIELD_SIZE.XL}]},
      ],
    },
  ],
  [MonitoringServiceType.MALWARE]: [
    {
      section: SENSITIVITY_AND_TARGETING_SECTIONS.EXECUTION,
      title: SensitivityAndTargetingSectionTitles[SENSITIVITY_AND_TARGETING_SECTIONS.EXECUTION],
      rows: [
        {
          size: null,
          fields: [{field: ServiceField.NUM_RETRIES, size: FIELD_SIZE.S}],
          warnings: [ServiceField.NUM_RETRIES],
        },
      ],
    },
    {
      section: SENSITIVITY_AND_TARGETING_SECTIONS.SLA,
      title: SensitivityAndTargetingSectionTitles[SENSITIVITY_AND_TARGETING_SECTIONS.SLA],
      rows: [
        {
          size: null,
          fields: [{field: ServiceField.UPTIME_SLA, size: FIELD_SIZE.XS}],
        },
        {size: null, fields: [{field: ServiceField.NOTES, size: FIELD_SIZE.XL}]},
      ],
    },
  ],
  [MonitoringServiceType.WHOIS]: [
    {
      section: SENSITIVITY_AND_TARGETING_SECTIONS.EXECUTION,
      title: SensitivityAndTargetingSectionTitles[SENSITIVITY_AND_TARGETING_SECTIONS.EXECUTION],
      rows: [
        {
          size: null,
          fields: [{field: ServiceField.NUM_RETRIES, size: FIELD_SIZE.S}],
          warnings: [ServiceField.NUM_RETRIES],
        },
      ],
    },
    {
      section: SENSITIVITY_AND_TARGETING_SECTIONS.SLA,
      title: SensitivityAndTargetingSectionTitles[SENSITIVITY_AND_TARGETING_SECTIONS.SLA],
      rows: [
        {
          size: null,
          fields: [{field: ServiceField.UPTIME_SLA, size: FIELD_SIZE.XS}],
        },
        {size: null, fields: [{field: ServiceField.NOTES, size: FIELD_SIZE.XL}]},
      ],
    },
  ],
  [MonitoringServiceType.RDAP]: [
    {
      section: SENSITIVITY_AND_TARGETING_SECTIONS.EXECUTION,
      title: SensitivityAndTargetingSectionTitles[SENSITIVITY_AND_TARGETING_SECTIONS.EXECUTION],
      rows: [
        {
          size: null,
          fields: [{field: ServiceField.NUM_RETRIES, size: FIELD_SIZE.S}],
          warnings: [ServiceField.NUM_RETRIES],
        },
      ],
    },
    {
      section: SENSITIVITY_AND_TARGETING_SECTIONS.SLA,
      title: SensitivityAndTargetingSectionTitles[SENSITIVITY_AND_TARGETING_SECTIONS.SLA],
      rows: [
        {
          size: null,
          fields: [{field: ServiceField.UPTIME_SLA, size: FIELD_SIZE.XS}],
        },
        {size: null, fields: [{field: ServiceField.NOTES, size: FIELD_SIZE.XL}]},
      ],
    },
  ],
  [MonitoringServiceType.SSL_CERT]: [
    {
      section: SENSITIVITY_AND_TARGETING_SECTIONS.EXECUTION,
      title: SensitivityAndTargetingSectionTitles[SENSITIVITY_AND_TARGETING_SECTIONS.EXECUTION],
      rows: [
        {
          size: null,
          fields: [{field: ServiceField.NUM_RETRIES, size: FIELD_SIZE.XS}],
          warnings: [ServiceField.NUM_RETRIES],
        },
        {
          size: null,
          fields: [
            {field: ServiceField.PORT, size: FIELD_SIZE.XS},
            {field: ServiceField.SSL_RESOLVE, size: FIELD_SIZE.S},
          ],
        },
      ],
    },
    {
      section: SENSITIVITY_AND_TARGETING_SECTIONS.SLA,
      title: SensitivityAndTargetingSectionTitles[SENSITIVITY_AND_TARGETING_SECTIONS.SLA],
      rows: [
        {
          size: null,
          fields: [{field: ServiceField.UPTIME_SLA, size: FIELD_SIZE.XS}],
        },
      ],
    },
    {
      section: SENSITIVITY_AND_TARGETING_SECTIONS.CERTIFICATE_VALIDATION,
      title: SensitivityAndTargetingSectionTitles[SENSITIVITY_AND_TARGETING_SECTIONS.CERTIFICATE_VALIDATION],
      rows: [
        {
          size: null,
          fields: [{field: ServiceField.SSL_MIN_VERSION, size: FIELD_SIZE.S}],
        },
        {
          size: null,
          fields: [{field: ServiceField.SSL_FINGERPRINT, size: FIELD_SIZE.XL}],
        },
        {size: null, fields: [{field: ServiceField.SSL_ISSUER, size: FIELD_SIZE.XL}]},
        {size: null, fields: [{field: ServiceField.SSL_MATCH, size: FIELD_SIZE.XL}]},
        {size: null, fields: [{field: ServiceField.SSL_CRL, size: FIELD_SIZE.XL}]},
        {size: null, fields: [{field: ServiceField.SSL_SELFSIGNED, size: FIELD_SIZE.XL}]},
        {size: null, fields: [{field: ServiceField.SSL_FIRST_ELEMENT, size: FIELD_SIZE.XL}]},
        {size: null, fields: [{field: ServiceField.SSL_IGNORE_AUTHORITY_WARNINGS, size: FIELD_SIZE.XL}]},
        {size: null, fields: [{field: ServiceField.SSL_IGNORE_SCT, size: FIELD_SIZE.XL}]},
        {size: null, fields: [{field: ServiceField.NOTES, size: FIELD_SIZE.XL}]},
      ],
    },
  ],
  [MonitoringServiceType.TCP]: [
    {
      section: SENSITIVITY_AND_TARGETING_SECTIONS.EXECUTION,
      title: SensitivityAndTargetingSectionTitles[SENSITIVITY_AND_TARGETING_SECTIONS.EXECUTION],
      rows: [
        {
          size: null,
          fields: [
            {field: ServiceField.SENSITIVITY, size: FIELD_SIZE.S},
            {field: ServiceField.NUM_RETRIES, size: FIELD_SIZE.S},
          ],
          warnings: [ServiceField.SENSITIVITY, ServiceField.NUM_RETRIES],
        },
        {
          size: null,
          fields: [
            {field: ServiceField.SEND_STRING, size: FIELD_SIZE.S},
            {field: ServiceField.ENCRYPTION, size: FIELD_SIZE.S},
          ],
        },
        {
          size: null,
          fields: [
            {field: ServiceField.EXPECT_STRING, size: FIELD_SIZE.S},
            {field: ServiceField.USE_IP_VERSION, size: FIELD_SIZE.XS},
          ],
        },
      ],
    },
    {
      section: SENSITIVITY_AND_TARGETING_SECTIONS.SLA,
      title: SensitivityAndTargetingSectionTitles[SENSITIVITY_AND_TARGETING_SECTIONS.SLA],
      rows: [
        {
          size: null,
          fields: [
            {field: ServiceField.UPTIME_SLA, size: FIELD_SIZE.XS},
            {field: ServiceField.RESPONSE_TIME_SLA, size: FIELD_SIZE.S},
          ],
        },
        {size: null, fields: [{field: ServiceField.NOTES, size: FIELD_SIZE.XL}]},
        {size: null, fields: [{field: ServiceField.INCLUDE_IN_GLOBAL_METRICS, size: FIELD_SIZE.L}]},
      ],
    },
  ],
  [MonitoringServiceType.DNS]: [
    {
      section: SENSITIVITY_AND_TARGETING_SECTIONS.EXECUTION,
      title: SensitivityAndTargetingSectionTitles[SENSITIVITY_AND_TARGETING_SECTIONS.EXECUTION],
      rows: [
        {
          size: FIELD_SIZE.XL,
          fields: [
            {field: ServiceField.SENSITIVITY, size: null},
            {field: ServiceField.NUM_RETRIES, size: null},
            {field: ServiceField.THRESHOLD, size: FIELD_SIZE.XS},
          ],
          warnings: [ServiceField.SENSITIVITY, ServiceField.NUM_RETRIES, ServiceField.THRESHOLD],
        },
      ],
    },
    {
      section: SENSITIVITY_AND_TARGETING_SECTIONS.SLA,
      title: SensitivityAndTargetingSectionTitles[SENSITIVITY_AND_TARGETING_SECTIONS.SLA],
      rows: [
        {
          size: null,
          fields: [
            {field: ServiceField.UPTIME_SLA, size: FIELD_SIZE.XS},
            {field: ServiceField.RESPONSE_TIME_SLA, size: FIELD_SIZE.S},
          ],
        },
        {size: null, fields: [{field: ServiceField.NOTES, size: FIELD_SIZE.XL}]},
        {size: null, fields: [{field: ServiceField.INCLUDE_IN_GLOBAL_METRICS, size: FIELD_SIZE.L}]},
      ],
    },
  ],
  [MonitoringServiceType.ICMP]: [
    {
      section: SENSITIVITY_AND_TARGETING_SECTIONS.EXECUTION,
      title: SensitivityAndTargetingSectionTitles[SENSITIVITY_AND_TARGETING_SECTIONS.EXECUTION],
      rows: [
        {
          size: FIELD_SIZE.XL,
          fields: [
            {field: ServiceField.SENSITIVITY, size: null},
            {field: ServiceField.NUM_RETRIES, size: null},
            {field: ServiceField.USE_IP_VERSION, size: FIELD_SIZE.XS},
          ],
          warnings: [ServiceField.SENSITIVITY, ServiceField.NUM_RETRIES],
        },
      ],
    },
    {
      section: SENSITIVITY_AND_TARGETING_SECTIONS.SLA,
      title: SensitivityAndTargetingSectionTitles[SENSITIVITY_AND_TARGETING_SECTIONS.SLA],
      rows: [
        {
          size: null,
          fields: [
            {field: ServiceField.UPTIME_SLA, size: FIELD_SIZE.XS},
            {field: ServiceField.RESPONSE_TIME_SLA, size: FIELD_SIZE.S},
          ],
        },
        {size: null, fields: [{field: ServiceField.NOTES, size: FIELD_SIZE.XL}]},
        {size: null, fields: [{field: ServiceField.INCLUDE_IN_GLOBAL_METRICS, size: FIELD_SIZE.L}]},
      ],
    },
  ],
  [MonitoringServiceType.NETWORK]: [
    {
      section: SENSITIVITY_AND_TARGETING_SECTIONS.EXECUTION,
      title: SensitivityAndTargetingSectionTitles[SENSITIVITY_AND_TARGETING_SECTIONS.EXECUTION],
      rows: [
        {
          size: FIELD_SIZE.XL,
          fields: [
            {field: ServiceField.SENSITIVITY, size: null},
            {field: ServiceField.NUM_RETRIES, size: null},
          ],
          warnings: [ServiceField.SENSITIVITY, ServiceField.NUM_RETRIES],
        },
      ],
    },
    {
      section: SENSITIVITY_AND_TARGETING_SECTIONS.SLA,
      title: SensitivityAndTargetingSectionTitles[SENSITIVITY_AND_TARGETING_SECTIONS.SLA],
      rows: [
        {
          size: null,
          fields: [
            {field: ServiceField.UPTIME_SLA, size: FIELD_SIZE.XS},
            {field: ServiceField.RESPONSE_TIME_SLA, size: FIELD_SIZE.S},
          ],
        },
        {size: null, fields: [{field: ServiceField.NOTES, size: FIELD_SIZE.XL}]},
        {size: null, fields: [{field: ServiceField.INCLUDE_IN_GLOBAL_METRICS, size: FIELD_SIZE.L}]},
      ],
    },
  ],
  [MonitoringServiceType.SSH]: [
    {
      section: SENSITIVITY_AND_TARGETING_SECTIONS.EXECUTION,
      title: SensitivityAndTargetingSectionTitles[SENSITIVITY_AND_TARGETING_SECTIONS.EXECUTION],
      rows: [
        {
          size: FIELD_SIZE.XL,
          fields: [
            {field: ServiceField.SENSITIVITY, size: null},
            {field: ServiceField.NUM_RETRIES, size: null},
            {field: ServiceField.USE_IP_VERSION, size: FIELD_SIZE.XS},
          ],
          warnings: [ServiceField.SENSITIVITY, ServiceField.NUM_RETRIES],
        },
      ],
    },
    {
      section: SENSITIVITY_AND_TARGETING_SECTIONS.SLA,
      title: SensitivityAndTargetingSectionTitles[SENSITIVITY_AND_TARGETING_SECTIONS.SLA],
      rows: [
        {
          size: null,
          fields: [
            {field: ServiceField.UPTIME_SLA, size: FIELD_SIZE.XS},
            {field: ServiceField.RESPONSE_TIME_SLA, size: FIELD_SIZE.S},
          ],
        },
        {size: null, fields: [{field: ServiceField.NOTES, size: FIELD_SIZE.XL}]},
        {size: null, fields: [{field: ServiceField.INCLUDE_IN_GLOBAL_METRICS, size: FIELD_SIZE.L}]},
      ],
    },
  ],
  [MonitoringServiceType.UDP]: [
    {
      section: SENSITIVITY_AND_TARGETING_SECTIONS.EXECUTION,
      title: SensitivityAndTargetingSectionTitles[SENSITIVITY_AND_TARGETING_SECTIONS.EXECUTION],
      rows: [
        {
          size: FIELD_SIZE.XL,
          fields: [
            {field: ServiceField.SENSITIVITY, size: null},
            {field: ServiceField.NUM_RETRIES, size: null},
            {field: ServiceField.USE_IP_VERSION, size: FIELD_SIZE.XS},
          ],
          warnings: [ServiceField.SENSITIVITY, ServiceField.NUM_RETRIES],
        },
        {
          size: FIELD_SIZE.XL,
          fields: [
            {field: ServiceField.SEND_STRING, size: null},
            {field: ServiceField.EXPECT_STRING, size: null},
          ],
        },
      ],
    },
    {
      section: SENSITIVITY_AND_TARGETING_SECTIONS.SLA,
      title: SensitivityAndTargetingSectionTitles[SENSITIVITY_AND_TARGETING_SECTIONS.SLA],
      rows: [
        {
          size: null,
          fields: [
            {field: ServiceField.UPTIME_SLA, size: FIELD_SIZE.XS},
            {field: ServiceField.RESPONSE_TIME_SLA, size: FIELD_SIZE.S},
          ],
        },
        {size: null, fields: [{field: ServiceField.NOTES, size: FIELD_SIZE.XL}]},
        {size: null, fields: [{field: ServiceField.INCLUDE_IN_GLOBAL_METRICS, size: FIELD_SIZE.L}]},
      ],
    },
  ],
  [MonitoringServiceType.NTP]: [
    {
      section: SENSITIVITY_AND_TARGETING_SECTIONS.EXECUTION,
      title: SensitivityAndTargetingSectionTitles[SENSITIVITY_AND_TARGETING_SECTIONS.EXECUTION],
      rows: [
        {
          size: null,
          fields: [
            {field: ServiceField.SENSITIVITY, size: FIELD_SIZE.S},
            {field: ServiceField.NUM_RETRIES, size: FIELD_SIZE.S},
          ],
          warnings: [ServiceField.SENSITIVITY, ServiceField.NUM_RETRIES],
        },
        {
          size: null,
          fields: [
            {field: ServiceField.USE_IP_VERSION, size: FIELD_SIZE.XS},
            {field: ServiceField.THRESHOLD, size: FIELD_SIZE.XS},
          ],
        },
      ],
    },
    {
      section: SENSITIVITY_AND_TARGETING_SECTIONS.SLA,
      title: SensitivityAndTargetingSectionTitles[SENSITIVITY_AND_TARGETING_SECTIONS.SLA],
      rows: [
        {
          size: null,
          fields: [
            {field: ServiceField.UPTIME_SLA, size: FIELD_SIZE.XS},
            {field: ServiceField.RESPONSE_TIME_SLA, size: FIELD_SIZE.S},
          ],
        },
        {size: null, fields: [{field: ServiceField.NOTES, size: FIELD_SIZE.XL}]},
        {size: null, fields: [{field: ServiceField.INCLUDE_IN_GLOBAL_METRICS, size: FIELD_SIZE.L}]},
      ],
    },
  ],
  [MonitoringServiceType.FTP]: [
    {
      section: SENSITIVITY_AND_TARGETING_SECTIONS.EXECUTION,
      title: SensitivityAndTargetingSectionTitles[SENSITIVITY_AND_TARGETING_SECTIONS.EXECUTION],
      rows: [
        {
          size: FIELD_SIZE.XL,
          fields: [
            {field: ServiceField.SENSITIVITY, size: null},
            {field: ServiceField.NUM_RETRIES, size: null},
            {field: ServiceField.THRESHOLD, size: FIELD_SIZE.XS},
          ],
          warnings: [ServiceField.SENSITIVITY, ServiceField.NUM_RETRIES, ServiceField.THRESHOLD],
        },
      ],
    },
    {
      section: SENSITIVITY_AND_TARGETING_SECTIONS.SLA,
      title: SensitivityAndTargetingSectionTitles[SENSITIVITY_AND_TARGETING_SECTIONS.SLA],
      rows: [
        {
          size: null,
          fields: [
            {field: ServiceField.UPTIME_SLA, size: FIELD_SIZE.XS},
            {field: ServiceField.RESPONSE_TIME_SLA, size: FIELD_SIZE.S},
          ],
        },
        {size: null, fields: [{field: ServiceField.NOTES, size: FIELD_SIZE.XL}]},
        {size: null, fields: [{field: ServiceField.INCLUDE_IN_GLOBAL_METRICS, size: FIELD_SIZE.L}]},
      ],
    },
  ],
  [MonitoringServiceType.SFTP]: [
    {
      section: SENSITIVITY_AND_TARGETING_SECTIONS.EXECUTION,
      title: SensitivityAndTargetingSectionTitles[SENSITIVITY_AND_TARGETING_SECTIONS.EXECUTION],
      rows: [
        {
          size: FIELD_SIZE.XL,
          fields: [
            {field: ServiceField.SENSITIVITY, size: null},
            {field: ServiceField.NUM_RETRIES, size: null},
            {field: ServiceField.THRESHOLD, size: FIELD_SIZE.XS},
          ],
          warnings: [ServiceField.SENSITIVITY, ServiceField.NUM_RETRIES, ServiceField.THRESHOLD],
        },
      ],
    },
    {
      section: SENSITIVITY_AND_TARGETING_SECTIONS.SLA,
      title: SensitivityAndTargetingSectionTitles[SENSITIVITY_AND_TARGETING_SECTIONS.SLA],
      rows: [
        {
          size: null,
          fields: [
            {field: ServiceField.UPTIME_SLA, size: FIELD_SIZE.XS},
            {field: ServiceField.RESPONSE_TIME_SLA, size: FIELD_SIZE.S},
          ],
        },
        {size: null, fields: [{field: ServiceField.NOTES, size: FIELD_SIZE.XL}]},
        {size: null, fields: [{field: ServiceField.INCLUDE_IN_GLOBAL_METRICS, size: FIELD_SIZE.L}]},
      ],
    },
  ],
  [MonitoringServiceType.POP]: [
    {
      section: SENSITIVITY_AND_TARGETING_SECTIONS.EXECUTION,
      title: SensitivityAndTargetingSectionTitles[SENSITIVITY_AND_TARGETING_SECTIONS.EXECUTION],
      rows: [
        {
          size: null,
          fields: [
            {field: ServiceField.PORT, size: FIELD_SIZE.XS},
            {field: ServiceField.ENCRYPTION, size: FIELD_SIZE.XS},
          ],
        },
        {
          size: null,
          fields: [{field: ServiceField.EXPECT_STRING, size: FIELD_SIZE.S}],
        },
        {
          size: FIELD_SIZE.XL,
          fields: [
            {field: ServiceField.SENSITIVITY, size: null},
            {field: ServiceField.NUM_RETRIES, size: null},
            {field: ServiceField.USE_IP_VERSION, size: FIELD_SIZE.XS},
          ],
          warnings: [ServiceField.SENSITIVITY, ServiceField.NUM_RETRIES, ServiceField.THRESHOLD],
        },
      ],
    },
    {
      section: SENSITIVITY_AND_TARGETING_SECTIONS.SLA,
      title: SensitivityAndTargetingSectionTitles[SENSITIVITY_AND_TARGETING_SECTIONS.SLA],
      rows: [
        {
          size: null,
          fields: [
            {field: ServiceField.UPTIME_SLA, size: FIELD_SIZE.XS},
            {field: ServiceField.RESPONSE_TIME_SLA, size: FIELD_SIZE.S},
          ],
        },
        {size: null, fields: [{field: ServiceField.NOTES, size: FIELD_SIZE.XL}]},
        {size: null, fields: [{field: ServiceField.INCLUDE_IN_GLOBAL_METRICS, size: FIELD_SIZE.L}]},
      ],
    },
  ],
  [MonitoringServiceType.IMAP]: [
    {
      section: SENSITIVITY_AND_TARGETING_SECTIONS.EXECUTION,
      title: SensitivityAndTargetingSectionTitles[SENSITIVITY_AND_TARGETING_SECTIONS.EXECUTION],
      rows: [
        {
          size: null,
          fields: [
            {field: ServiceField.PORT, size: FIELD_SIZE.XS},
            {field: ServiceField.ENCRYPTION, size: FIELD_SIZE.XS},
          ],
        },
        {
          size: null,
          fields: [{field: ServiceField.EXPECT_STRING, size: FIELD_SIZE.S}],
        },
        {
          size: FIELD_SIZE.XL,
          fields: [
            {field: ServiceField.SENSITIVITY, size: null},
            {field: ServiceField.NUM_RETRIES, size: null},
            {field: ServiceField.USE_IP_VERSION, size: FIELD_SIZE.XS},
          ],
          warnings: [ServiceField.SENSITIVITY, ServiceField.NUM_RETRIES, ServiceField.THRESHOLD],
        },
      ],
    },
    {
      section: SENSITIVITY_AND_TARGETING_SECTIONS.SLA,
      title: SensitivityAndTargetingSectionTitles[SENSITIVITY_AND_TARGETING_SECTIONS.SLA],
      rows: [
        {
          size: null,
          fields: [
            {field: ServiceField.UPTIME_SLA, size: FIELD_SIZE.XS},
            {field: ServiceField.RESPONSE_TIME_SLA, size: FIELD_SIZE.S},
          ],
        },
        {size: null, fields: [{field: ServiceField.NOTES, size: FIELD_SIZE.XL}]},
        {size: null, fields: [{field: ServiceField.INCLUDE_IN_GLOBAL_METRICS, size: FIELD_SIZE.L}]},
      ],
    },
  ],
  [MonitoringServiceType.SMTP]: [
    {
      section: SENSITIVITY_AND_TARGETING_SECTIONS.EXECUTION,
      title: SensitivityAndTargetingSectionTitles[SENSITIVITY_AND_TARGETING_SECTIONS.EXECUTION],
      rows: [
        {
          size: null,
          fields: [
            {field: ServiceField.PORT, size: FIELD_SIZE.XS},
            {field: ServiceField.ENCRYPTION, size: FIELD_SIZE.XS},
          ],
        },
        {
          size: null,
          fields: [{field: ServiceField.EXPECT_STRING, size: FIELD_SIZE.S}],
        },
        {
          size: FIELD_SIZE.XL,
          fields: [
            {field: ServiceField.SENSITIVITY, size: null},
            {field: ServiceField.NUM_RETRIES, size: null},
            {field: ServiceField.USE_IP_VERSION, size: FIELD_SIZE.XS},
          ],
          warnings: [ServiceField.SENSITIVITY, ServiceField.NUM_RETRIES, ServiceField.THRESHOLD],
        },
      ],
    },
    {
      section: SENSITIVITY_AND_TARGETING_SECTIONS.SLA,
      title: SensitivityAndTargetingSectionTitles[SENSITIVITY_AND_TARGETING_SECTIONS.SLA],
      rows: [
        {
          size: null,
          fields: [
            {field: ServiceField.UPTIME_SLA, size: FIELD_SIZE.XS},
            {field: ServiceField.RESPONSE_TIME_SLA, size: FIELD_SIZE.S},
          ],
        },
        {size: null, fields: [{field: ServiceField.NOTES, size: FIELD_SIZE.XL}]},
        {size: null, fields: [{field: ServiceField.INCLUDE_IN_GLOBAL_METRICS, size: FIELD_SIZE.L}]},
      ],
    },
  ],
  [MonitoringServiceType.HEARTBEAT]: [
    {
      section: SENSITIVITY_AND_TARGETING_SECTIONS.SLA,
      title: SensitivityAndTargetingSectionTitles[SENSITIVITY_AND_TARGETING_SECTIONS.SLA],
      rows: [
        {
          size: null,
          fields: [
            {field: ServiceField.UPTIME_SLA, size: FIELD_SIZE.XS},
            {field: ServiceField.RESPONSE_TIME_SLA, size: FIELD_SIZE.S},
          ],
        },
        {size: null, fields: [{field: ServiceField.NOTES, size: FIELD_SIZE.XL}]},
        {size: null, fields: [{field: ServiceField.INCLUDE_IN_GLOBAL_METRICS, size: FIELD_SIZE.L}]},
      ],
    },
  ],
  [MonitoringServiceType.WEBHOOK]: [
    {
      section: SENSITIVITY_AND_TARGETING_SECTIONS.SLA,
      title: SensitivityAndTargetingSectionTitles[SENSITIVITY_AND_TARGETING_SECTIONS.SLA],
      rows: [
        {
          size: null,
          fields: [
            {field: ServiceField.UPTIME_SLA, size: FIELD_SIZE.XS},
            {field: ServiceField.RESPONSE_TIME_SLA, size: FIELD_SIZE.S},
          ],
        },
        {size: null, fields: [{field: ServiceField.NOTES, size: FIELD_SIZE.XL}]},
        {size: null, fields: [{field: ServiceField.INCLUDE_IN_GLOBAL_METRICS, size: FIELD_SIZE.L}]},
      ],
    },
  ],
  [MonitoringServiceType.TRANSACTION]: [
    {
      section: SENSITIVITY_AND_TARGETING_SECTIONS.EXECUTION,
      title: SensitivityAndTargetingSectionTitles[SENSITIVITY_AND_TARGETING_SECTIONS.EXECUTION],
      rows: [
        {
          size: FIELD_SIZE.XL,
          fields: [
            {field: ServiceField.SENSITIVITY, size: null},
            {field: ServiceField.NUM_RETRIES, size: null},
            {field: ServiceField.THRESHOLD, size: FIELD_SIZE.XS},
          ],
          warnings: [ServiceField.SENSITIVITY, ServiceField.NUM_RETRIES, ServiceField.THRESHOLD],
        },
      ],
    },
    {
      section: SENSITIVITY_AND_TARGETING_SECTIONS.SLA,
      title: SensitivityAndTargetingSectionTitles[SENSITIVITY_AND_TARGETING_SECTIONS.SLA],
      rows: [
        {
          size: null,
          fields: [
            {field: ServiceField.UPTIME_SLA, size: FIELD_SIZE.XS},
            {field: ServiceField.RESPONSE_TIME_SLA, size: FIELD_SIZE.S},
          ],
        },
        {size: null, fields: [{field: ServiceField.NOTES, size: FIELD_SIZE.XL}]},
        {size: null, fields: [{field: ServiceField.INCLUDE_IN_GLOBAL_METRICS, size: FIELD_SIZE.L}]},
      ],
    },
  ],
  [MonitoringServiceType.API]: [
    {
      section: SENSITIVITY_AND_TARGETING_SECTIONS.EXECUTION,
      title: SensitivityAndTargetingSectionTitles[SENSITIVITY_AND_TARGETING_SECTIONS.EXECUTION],
      rows: [
        {
          size: null,
          fields: [
            {field: ServiceField.SENSITIVITY, size: FIELD_SIZE.S},
            {field: ServiceField.NUM_RETRIES, size: FIELD_SIZE.XS},
          ],
          warnings: [ServiceField.SENSITIVITY, ServiceField.NUM_RETRIES],
        },
        {
          size: null,
          fields: [
            {field: ServiceField.USE_IP_VERSION, size: FIELD_SIZE.XS},
            {field: ServiceField.THRESHOLD, size: FIELD_SIZE.XS},
          ],
        },
      ],
    },
    {
      section: SENSITIVITY_AND_TARGETING_SECTIONS.SLA,
      title: SensitivityAndTargetingSectionTitles[SENSITIVITY_AND_TARGETING_SECTIONS.SLA],
      rows: [
        {
          size: null,
          fields: [
            {field: ServiceField.UPTIME_SLA, size: FIELD_SIZE.XS},
            {field: ServiceField.RESPONSE_TIME_SLA, size: FIELD_SIZE.S},
          ],
        },
        {size: null, fields: [{field: ServiceField.NOTES, size: FIELD_SIZE.XL}]},
        {size: null, fields: [{field: ServiceField.INCLUDE_IN_GLOBAL_METRICS, size: FIELD_SIZE.L}]},
      ],
    },
  ],
  [MonitoringServiceType.PAGESPEED]: [
    {
      section: SENSITIVITY_AND_TARGETING_SECTIONS.EXECUTION,
      title: SensitivityAndTargetingSectionTitles[SENSITIVITY_AND_TARGETING_SECTIONS.EXECUTION],
      rows: [
        {
          size: null,
          fields: [{field: ServiceField.NUM_RETRIES, size: FIELD_SIZE.XS}],
          warnings: [ServiceField.NUM_RETRIES],
        },
        {size: null, fields: [{field: ServiceField.NOTES, size: FIELD_SIZE.XL}]},
      ],
    },
  ],
  [MonitoringServiceType.RUM2]: [
    {
      section: SENSITIVITY_AND_TARGETING_SECTIONS.TRACKING,
      title: SensitivityAndTargetingSectionTitles[SENSITIVITY_AND_TARGETING_SECTIONS.TRACKING],
      rows: [
        {
          size: null,
          fields: [
            {
              field: ServiceField.RUM_EXCLUDE_USER_AGENTS,
              size: FIELD_SIZE.XL,
            },
          ],
        },
        {
          size: null,
          fields: [
            {
              field: ServiceField.RUM_EXTERNAL_DOMAINS,
              size: FIELD_SIZE.XL,
            },
          ],
          warnings: [ServiceField.RUM_EXTERNAL_DOMAINS],
        },
        {
          size: null,
          fields: [
            {
              field: ServiceField.RUM_URL_GROUPS,
              size: FIELD_SIZE.XL,
            },
          ],
        },
        {
          size: null,
          fields: [
            {
              field: ServiceField.RUM_IS_AJAX_DISABLED,
              size: FIELD_SIZE.XL,
            },
          ],
          warnings: [ServiceField.RUM_IS_AJAX_DISABLED],
        },
        {
          size: null,
          fields: [
            {
              field: ServiceField.RUM_EXCLUDE_GROUP_PARAMS,
              size: FIELD_SIZE.XL,
            },
          ],
        },
      ],
    },
    {
      section: SENSITIVITY_AND_TARGETING_SECTIONS.SLA,
      title: SensitivityAndTargetingSectionTitles[SENSITIVITY_AND_TARGETING_SECTIONS.SLA],
      rows: [
        {
          size: null,
          fields: [{field: ServiceField.UPTIME_SLA, size: FIELD_SIZE.XS}],
        },
        {size: null, fields: [{field: ServiceField.NOTES, size: FIELD_SIZE.XL}]},
        {size: null, fields: [{field: ServiceField.INCLUDE_IN_GLOBAL_METRICS, size: FIELD_SIZE.L}]},
      ],
    },
  ],
};

export const RUM_SNIPPET_PLACEMENT_HINT =
  "Place the HTML code snippet before any scripts you wish to track with RUM, usually within the <head> element.";
